import type { App } from "../app.js";
import { memberName } from "../app.js";
import { formatDate, h, notice, plural, replace } from "../dom.js";
import { icon } from "../icons.js";
import { readProjectFile } from "../studio/project-file.js";
import { autoProjectName, inspirationUrl, newPreparation, OBJECTIVE_INFO } from "../prepare/model.js";
import { newDoc, normalizeDoc, preparedDoc, type Objective } from "../studio/model.js";
import type { ProjectSummary } from "../studio/backend.js";
import { loadFonts } from "../studio/fonts.js";
import { ImageStore } from "../studio/images.js";
import { HEIGHT, renderSlide, WIDTH } from "../studio/render.js";
import { draftIds } from "../studio/session.js";

const SHORT_OBJECTIVE: Record<Objective, string> = { follow: "Abonnés", share: "Partages", save: "Enregistrements", question: "Commentaires" };
type Filter = "all" | "prep" | "editor";

/** « Atelier »: the carousels being made, shared by the members, newest first, as cards with their cover. */
export function studioListView(app: App): HTMLElement {
  const studio = app.connection.studio;
  const root = h("div", { class: "atelier" }, h("p", { class: "muted" }, "Chargement…"));
  const status = h("div", { "aria-live": "polite" });
  let filter: Filter = "all";
  let projects: ProjectSummary[] = [];
  const grid = h("ul", { class: "st-projects", "aria-label": "Projets" });

  const fileInput = h("input", {
    type: "file",
    accept: ".zip,application/zip",
    class: "sr-only",
    "aria-label": "Importer un fichier de projet",
    onchange: async () => {
      const file = fileInput.files?.[0];
      fileInput.value = "";
      if (!file) return;
      replace(status, h("p", { class: "muted" }, "Lecture du fichier de projet…"));
      try {
        const imported = await readProjectFile(file);
        for (const [sha, blob] of imported.images) await studio.putImage(sha, blob);
        const project = await studio.createProject(`${imported.name} (importé)`.slice(0, 120), imported.doc);
        app.navigate(`#/atelier/${project.id}`);
      } catch (error) {
        replace(status, notice("bad", "Import impossible", error instanceof Error ? error.message : String(error)));
      }
    },
  });

  const tabs = h(
    "div",
    { class: "tabs", role: "tablist", "aria-label": "Filtrer les projets" },
    ([
      ["all", "Tous"],
      ["prep", "Préparations"],
      ["editor", "Dans l'éditeur"],
    ] as Array<[Filter, string]>).map(([value, label]) =>
      h(
        "button",
        {
          type: "button",
          role: "tab",
          "aria-selected": String(value === filter),
          onclick: (e: Event) => {
            filter = value;
            for (const b of tabs.querySelectorAll("button")) b.setAttribute("aria-selected", String(b === e.currentTarget));
            showGrid();
          },
        },
        label,
      ),
    ),
  );

  const covers = new Map<string, HTMLCanvasElement>();
  const showGrid = () => {
    const drafts = draftIds();
    const shown = projects.filter((p) => filter === "all" || (filter === "prep" ? !p.slide_count : p.slide_count > 0));
    replace(
      grid,
      shown.map((p) => {
        const prep = !p.slide_count;
        const cover = covers.get(p.id) ?? h("canvas", { width: "432", height: "540", class: "st-thumb", "aria-hidden": "true" });
        covers.set(p.id, cover);
        return h(
          "li",
          null,
          h(
            "a",
            { class: `st-project${prep ? " is-prep" : ""}`, href: `#/atelier/${p.id}` },
            prep ? h("span", { class: "st-thumb st-thumb-prep", "aria-hidden": "true" }, icon("file"), h("span", null, "Préparation")) : cover,
            h("strong", null, p.name),
            h(
              "span",
              { class: "chips" },
              prep ? h("span", { class: "chip yellow" }, "Préparation en cours") : h("span", { class: "chip" }, plural(p.slide_count, "slide", "slides")),
              drafts.has(p.id) ? h("span", { class: "chip warn" }, "Modifications locales") : null,
            ),
            h("span", { class: "muted st-meta" }, `${formatDate(p.updated_at)} · ${memberName(app, p.updated_by)}`),
          ),
        );
      }),
    );
    if (!shown.length) replace(grid, h("li", { class: "empty" }, h("p", { class: "muted" }, projects.length ? "Rien dans ce filtre." : "Aucun carrousel pour l'instant.")));
  };

  void (async () => {
    try {
      projects = await studio.listProjects();
      replace(
        root,
        h(
          "div",
          { class: "page-head" },
          h("h1", null, "Vos carrousels"),
          h("div", { class: "row" }, h("label", { class: "btn" }, fileInput, icon("upload"), h("span", null, "Importer")), h("a", { class: "btn primary", href: "#/atelier/nouveau" }, icon("plus"), h("span", null, "Nouveau carrousel"))),
        ),
        projects.length ? tabs : null,
        status,
        grid,
      );
      showGrid();
      void drawCovers(app, projects.filter((p) => p.slide_count > 0), covers);
    } catch (error) {
      replace(root, notice("bad", "Atelier indisponible", error instanceof Error ? error.message : String(error), h("button", { class: "btn", onclick: () => app.refresh() }, "Réessayer")));
    }
  })();
  return root;
}

/** Each project's cover, drawn by the Atelier's renderer (the same as the export), one after the other. */
async function drawCovers(app: App, projects: ProjectSummary[], covers: Map<string, HTMLCanvasElement>): Promise<void> {
  const fonts = await loadFonts();
  if (!fonts.ok) return;
  const store = new ImageStore(app.connection.studio);
  const scratch = document.createElement("canvas");
  scratch.width = WIDTH;
  scratch.height = HEIGHT;
  for (const p of projects) {
    try {
      const doc = normalizeDoc((await app.connection.studio.getProject(p.id)).doc);
      const index = Math.max(0, doc.slides.findIndex((s) => s.kind === "cover"));
      const slide = doc.slides[index];
      if (!slide) continue;
      if (slide.image) await store.load(slide.image).catch(() => undefined);
      renderSlide(scratch.getContext("2d")!, doc, index, { images: store.images, monogram: null });
      const target = covers.get(p.id);
      target?.getContext("2d")!.drawImage(scratch, 0, 0, target.width, target.height);
      target?.classList.add("drawn");
    } catch {
      // a cover that cannot be drawn keeps its placeholder: the project still opens
    }
  }
}

/** « Nouveau carrousel »: prepare it with a conversation, or start from a blank page. */
export function studioNewView(app: App): HTMLElement {
  const studio = app.connection.studio;
  const status = h("div", { "aria-live": "polite" });

  const idea = h("textarea", { rows: "3", maxlength: "1000", placeholder: "Ex. : 7 inventions qui semblent venir du futur", "aria-label": "Idée du carrousel à préparer" });
  const link = h("input", { type: "url", maxlength: "500", placeholder: "https://…", "aria-label": "Lien d'inspiration" });
  const objectiveName = "prep-objective";
  const prepare = h(
    "form",
    {
      class: "panel new-card",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        const button = prepare.querySelector<HTMLButtonElement>("button[type=submit]")!;
        if (button.disabled) return;
        button.disabled = true;
        try {
          const text = idea.value.trim();
          const url = inspirationUrl(link.value);
          if (!text && !url) throw new Error("Donne une idée ou un lien d'inspiration.");
          const objective = (prepare.querySelector<HTMLInputElement>(`input[name=${objectiveName}]:checked`)?.value ?? "follow") as Objective;
          const prep = newPreparation({ idea: text, inspiration_url: url, objective, by: app.member.user_id });
          const project = await studio.createProject(autoProjectName(text, url), preparedDoc(prep));
          app.navigate(`#/atelier/${project.id}`);
        } catch (error) {
          replace(status, notice("bad", "Préparation impossible", error instanceof Error ? error.message : String(error)));
          button.disabled = false;
        }
      },
    },
    h("h2", null, "Préparer avec l'IA"),
    idea,
    h(
      "fieldset",
      { class: "st-choice" },
      h("legend", null, "Objectif"),
      (Object.keys(OBJECTIVE_INFO) as Objective[]).map((o) => h("label", null, h("input", { type: "radio", name: objectiveName, value: o, checked: o === "follow", "aria-label": OBJECTIVE_INFO[o].label }), h("span", null, SHORT_OBJECTIVE[o]))),
    ),
    h("details", { class: "new-link" }, h("summary", null, "Lien d'inspiration"), link),
    h("button", { class: "btn primary big", type: "submit" }, "Préparer un carrousel", icon("next")),
  );

  const name = h("input", { type: "text", maxlength: "120", placeholder: "Nom du carrousel", "aria-label": "Nom du nouveau carrousel", required: true });
  const blank = h(
    "form",
    {
      class: "panel new-card",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        const button = blank.querySelector("button")!;
        button.disabled = true;
        try {
          const project = await studio.createProject(name.value.trim() || "Sans titre", newDoc());
          app.navigate(`#/atelier/${project.id}`);
        } catch (error) {
          replace(status, notice("bad", "Création impossible", error instanceof Error ? error.message : String(error)));
          button.disabled = false;
        }
      },
    },
    h("h2", null, "Page blanche"),
    name,
    h("button", { class: "btn", type: "submit" }, "Nouveau carrousel"),
  );

  return h(
    "div",
    { class: "atelier-new" },
    h("div", { class: "page-head" }, h("a", { class: "btn icon-only", href: "#/atelier", "aria-label": "Retour aux projets" }, icon("back")), h("h1", null, "Nouveau carrousel")),
    status,
    h("div", { class: "new-grid" }, prepare, blank),
  );
}
